<template>
  <div
    v-if="assignedCoach?.fullName"
    class="i-coach-profile-details"
  >
    <div class="main">
      <div class="left">
        <div class="image-wrap">
          <img
            class="image"
            :src="assignedCoach.profilePhotoUrl || ''"
          />

          <template v-if="!!assignedCoach.introVideoUrl">
            <div
              class="play-video"
              @click="data.lightBoxVideo = assignedCoach.introVideoUrl || ''"
            ></div>
          </template>
        </div>
        <div class="details">
          <div class="coach">{{ assignedCoach.fullName || "Your Coach" }} <img :src="iconCheckBlue" /></div>
          <iBaseCopyText
            class="coach-email"
            :text="assignedCoach.email"
          />
          <div class="rating">
            <img :src="iconStars1" />
            {{ assignedCoach.rating }} stars
            <span
              class="review-cta"
              @click="data.showReviewModal = true"
              >(<u>{{ data.assignedCoachReviews?.length || numberOfReviews }} reviews</u>)</span
            >
          </div>
        </div>
      </div>
      <div
        v-if="mainCtaData.length > 0"
        class="right"
        :class="{ grey: mainCtaData[0].label === 'Buy Resume Package' }"
      >
        <div
          v-if="mainCtaData[0].label === 'Buy Resume Package'"
          class="resume"
        >
          <div>Want an ATS crafted Resume?</div>
          <div class="tiny">Book at least 24 hours in advance.</div>
        </div>
        <div class="ctas">
          <div class="surface">
            <iBaseButton
              v-if="mainCtaData.filter((e, i) => i != 0).length > 0"
              class="more"
              v-bind="OTHER_BUTTON_STYLE"
              text=""
              :icon-url="iconDotsHorizontal"
            />
            <iBaseButton
              v-bind="mainButtonStyle"
              :text="mainCtaData[0].label"
              :icon-url="mainCtaData[0].iconWhite"
              icon-width="22px"
              @clicked="mainCtaData[0].action()"
            />
          </div>
          <div class="wrapper">
            <div class="options">
              <div
                v-for="cta in mainCtaData.filter((e, i) => i != 0)"
                :key="cta.label"
                class="option"
                @click="cta.action()"
              >
                <img :src="cta.icon" />
                {{ cta.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="data.lightBoxVideo">
      <iBaseLightBox @close="data.lightBoxVideo = ''">
        <video
          :src="data.lightBoxVideo"
          autoplay
          controls
          class="light-box-video"
          type="video/mp4"
        ></video>
      </iBaseLightBox>
    </template>

    <iBaseModal
      class="embed"
      :visible="data.showCalcomEmbedModal"
      :fully-customisable="true"
      max-width="100%"
      @click-on-close="data.showCalcomEmbedModal = false"
    >
      <div class="confirmation-modal">
        <iCalcomScheduler
          :on-booking-successful="bookingSuccessful"
          :embed-link="data.calcomEmbedLink"
          version="inline"
        />
      </div>
    </iBaseModal>

    <iBaseModal
      :visible="data.showReviewModal"
      :fully-customisable="true"
      :enable-bg-close="true"
      max-width="840px"
      @click-on-close="data.showReviewModal = false"
    >
      <div class="review-modal">
        <Carousel
          :autoplay="30000"
          :items-to-scroll="1"
          :items-to-show="isDesktopOrLarger ? 2 : 1"
          class="reviews-carousel"
          pause-autoplay-on-hover
          wrap-around
          snap-align="start"
        >
          <Slide
            v-for="(review, index) in data.assignedCoachReviews?.length ? data.assignedCoachReviews : reviewData"
            :key="index"
          >
            <div class="review">
              <div class="info">
                <img
                  v-if="review.userImageUrl"
                  class="rev-image"
                  :src="review.userImageUrl"
                />
                <div
                  v-else
                  class="initials"
                >
                  {{ getInitials(review.clientName) }}
                </div>
                <div class="user-info">
                  <strong>{{ review.clientName }}</strong>
                  <div class="location"><img :src="iconLocation" /> USA</div>
                </div>
              </div>
              <div class="star-date">
                <img
                  class="stars"
                  :src="iconStars"
                />
                <div class="date">
                  {{
                    new Date(review.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    })
                  }}
                </div>
              </div>
              <div class="body">
                <div
                  class="progress"
                  v-html="review.title"
                ></div>
                <div class="comment">{{ review.body }}</div>
              </div>
            </div>
          </Slide>
          <template #addons>
            <navigation>
              <template #next>
                <span>
                  <img
                    :src="iconArrowRightGray"
                    alt="next-icon"
                  />
                </span>
              </template>
              <template #prev>
                <span>
                  <img
                    :src="iconArrowLeftGray"
                    alt="prev-icon"
                  />
                </span>
              </template>
            </navigation>
          </template>
        </Carousel>
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { useCoachingStore } from "@/stores/coaching"
import { useUserStore } from "@/stores/user"
import { black200, bluePurpleGradient, white } from "@/styles/colors"
import { breakpointLg, breakpointMd } from "@/styles/variables"
import { computed, onMounted, reactive } from "vue"
import { useRouter } from "vue-router"
import { Carousel, Navigation, Slide } from "vue3-carousel"

import getPaymentAction from "@/composables/getPaymentAction"
import useRandomizedArray from "@/composables/randomizeArray"
import useIsScreenSize from "@/composables/useIsScreenSize"
import useRandomNumber from "@/composables/useRandomNumber"

import { GlobalVariables } from "@/helpers/globalVariables"
// import UrlService from "@/services/urlService"
import { UserFlow } from "@/services/gtmService"
import api from "@/services/apiClient"

import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseCopyText from "@/components/base/BaseCopyText.vue"
import iBaseLightBox from "@/components/base/BaseLightBox.vue"
import iBaseModal from "@/components/base/BaseModal.vue"
import iCalcomScheduler from "@/components/global/CalcomScheduler.vue"

// ASSETS
import iconArrowLeftGray from "@/assets/icons/icon-arrow-left-gray.svg"
import iconArrowRightGray from "@/assets/icons/icon-arrow-right-gray.svg"
import iconCalendarRepeatWhite from "@/assets/icons/icon-calendar-repeat-white.svg"
import iconCalendarRepeat from "@/assets/icons/icon-calendar-repeat.svg"
// import iconCalendarPlusWhite from "@/assets/icons/icon-calendar-plus-white.svg"
// import iconCalendarPlus from "@/assets/icons/icon-calendar-plus.svg"
import iconCertificateWhite from "@/assets/icons/icon-certificate-white.svg"
import iconCertificate from "@/assets/icons/icon-certificate.svg"
// import iconChangeWhite from "@/assets/icons/icon-change-white.svg"
// import iconChange from "@/assets/icons/icon-change.svg"
import iconDotsHorizontal from "@/assets/icons/icon-dots-horizontal.svg"
import iconFilePlusWhite from "@/assets/icons/icon-file-plus-white.svg"
import iconFilePlus from "@/assets/icons/icon-file-plus.svg"
import iconStars1 from "@/assets/icons/icon-five-slashed-stars.svg"
import iconStars from "@/assets/icons/icon-five-stars.svg"
import iconLocation from "@/assets/icons/icon-profile-location-black.svg"
import iconCheckBlue from "@/assets/icons/icon-check-blue.svg"
import imageBrenden from "@/assets/images/Brenden-trustpilot.png"
import imageChristine from "@/assets/images/Christine-trustpilot.png"
import imageCory from "@/assets/images/Cory-trustpilot.png"
import imageDaniel from "@/assets/images/Daniel-trustpilot.png"
import imageDavid from "@/assets/images/David-trustpilot.png"
import imageJulie from "@/assets/images/Julie-trustpilot.png"
import imageTristan from "@/assets/images/Tristan-trustpilot.png"

// TYPES
import StripePrice from "@/shared/enums/StripePrice"
import { CoachReviews } from "@/shared/types/Coach"

// STORES AND ROUTES
const coachingStore = useCoachingStore()
const userStore = useUserStore()
const router = useRouter()

// COMPOSABLES
const isTabletOrLarger = useIsScreenSize(breakpointMd)
const isDesktopOrLarger = useIsScreenSize(breakpointLg)
const numberOfReviews = useRandomNumber(coachingStore.getAssignedCoach.fullName || "Full Name", 17, 35)

// CONSTANTS
const OTHER_BUTTON_STYLE = {
  backgroundColor: "transparent",
  borderRadius: "8px",
  fontWeight: "500",
  textColor: black200,
  width: "fit-content",
  padding: "9px 9px",
  size: "small",
  version: "outline"
}
// const ZIGPOLL_LINK = "https://survey.zigpoll.com/2tVLrZ1upJBnDCLQ3/2worxsN49rTtX4Brd"

// DATA
const data = reactive({
  lightBoxVideo: "",
  showCalcomEmbedModal: false,
  calcomEmbedLink: "",
  showReviewModal: false,
  assignedCoachReviews: [] as CoachReviews[]
})

// COMPUTED
const isExecutiveOnce = computed(() => userStore.isCoachingOnce && userStore.hasPurchasedExecutiveDiscovery)
const isExecutiveSubscribedOrCanceled = computed(
  () => userStore.isCoachingExecutiveOrProfessionalSubscriber || userStore.hasPurchasedExecutiveSubscription
)
const mainButtonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isTabletOrLarger.value ? "210px" : "100%",
    padding: "10px 16px",
    size: "small"
  }
})
const assignedCoach = computed(() => coachingStore.getAssignedCoach)
const reviewData = computed(() => {
  const coachFirstName = assignedCoach.value?.fullName?.split(" ")[0]
  return useRandomizedArray([
    {
      clientName: "Christine S.",
      date: "2025-12-11",
      userImageUrl: imageChristine,
      title: `Finding direction and <strong>confidence</strong> with ${coachFirstName}`,
      body: `I stumbled across Jobtest during my Google research while contemplating a career shift. After taking the career test, I signed up for biweekly sessions with ${coachFirstName}. Beyond just providing accountability, ${coachFirstName} guided me to online and local resources I wasn't aware of and gave me the confidence to pursue a career in interior design. ${coachFirstName} is incredibly kind and supportive, celebrating my progress each time we meet. I’ve gained so much direction and stability, and I hope my future job suits me as well as ${coachFirstName}'s does to them!`
    },
    {
      clientName: "Brenden C.",
      date: "2025-12-01",
      userImageUrl: imageBrenden,
      title: `Pursuing a <strong>career change</strong> with ${coachFirstName}.`,
      body: `I was looking for a career change, and Jobtest really helped me clarify my career interests better than any other test. The offer to speak with ${coachFirstName} was super helpful. ${coachFirstName} was extremely friendly and insightful, guiding me on the next steps I should take. I now feel more confident with a guided plan to achieve my goals.`
    },
    {
      clientName: "Julie B.",
      date: "2025-11-25",
      userImageUrl: imageJulie,
      title: `<strong>Transitioning to a new career</strong> with ${coachFirstName}.`,
      body: `I had the good fortune of meeting with ${coachFirstName}, who really understands what it's like to make a career transition. ${coachFirstName} asked great questions, offered valuable insights, and provided actionable next steps. As a former teacher like me, ${coachFirstName} brought both optimism and expertise to our sessions. I'm now feeling much more hopeful about my career journey.`
    },
    {
      clientName: "Tristan B.",
      date: "2025-11-18",
      userImageUrl: imageTristan,
      title: `<strong>Moving forward</strong> with ${coachFirstName}.`,
      body: `I was honestly apprehensive about trying this; it could very easily be a scam. But after taking the assessment, I got paired with ${coachFirstName}, who truly listened to me, and we made a great connection right away. ${coachFirstName} introduced me to many things, including positions and industries I had never even heard of. I’m quite excited to see what the future holds, and I think Jobtest is helping me move in the right direction.`
    },
    {
      clientName: "Daniel B.",
      date: "2025-10-16",
      userImageUrl: imageDaniel,
      title: `Exploring <strong>new career paths</strong> with ${coachFirstName}.`,
      body: `Today, ${coachFirstName} was incredibly thorough and positive, helping me navigate the results from jobtest.org and offering valuable insights into potential careers. ${coachFirstName}'s dedication made me feel truly valued, and our conversation felt like a discussion between good friends, backed by accurate test data. I’m grateful for the time and extra effort ${coachFirstName} put in to help me explore new perspectives and potential roles.`
    },
    {
      clientName: "Cory O.",
      date: "2025-09-07",
      userImageUrl: imageCory,
      title: `<strong>Rediscovering purpose</strong> with ${coachFirstName}`,
      body: `Jobtest uses a unique scoring mechanism that opened my eyes to careers I hadn't considered before. I chose the coaching option, and ${coachFirstName} helped me think deeply about careers, applications, resumes, and the job market in a whole new way. Thanks to ${coachFirstName} and Jobtest, I'm now on a journey to find work that inspires me, has a real impact, and aligns with who I am as a person. This has been a powerful experience in my mid-30s, realizing that monetary success alone isn't enough.`
    },
    {
      clientName: "David F.",
      date: "2025-09-24",
      userImageUrl: imageDavid,
      title: `From skepticism to <strong>success</strong> with ${coachFirstName}`,
      body: `I was skeptical at first, but ${coachFirstName} changed my mind almost immediately after our first conversation. I was just expecting some quick resume tips, but ${coachFirstName} ended up coaching me for a couple of months, meeting every other week. Ultimately, it's what you make of it, but I’d say ${coachFirstName} is one of the best coaches in the game.`
    }
  ])
})
const mainCtaData = computed(() => {
  const ctaData = [
    {
      label: userStore.isCoachingOnce ? "Subscribe" : "Resubscribe",
      icon: iconCalendarRepeat,
      iconWhite: iconCalendarRepeatWhite,
      visible:
        !userStore.getCoachingCredits &&
        (userStore.isCoachingCancelled || (userStore.isCoachingOnce && !userStore.hasPurchasedExecutiveDiscovery)),
      action: goToSelectPlan
    },
    {
      label: "Take the Career Test",
      icon: iconCertificate,
      iconWhite: iconCertificateWhite,
      visible: !userStore.isReportUser,
      action: clickOnTakeTest
    },
    {
      label: "Buy Resume Package",
      icon: iconFilePlus,
      iconWhite: iconFilePlusWhite,
      visible: !userStore.userData.hasResumePackage,
      action: () => firePaymentAction(StripePrice.RESUME_COACHING_ADDON, UserFlow.RESUME)
    }
    // {
    //   label: "Buy an Extra Session",
    //   icon: iconCalendarPlus,
    //   iconWhite: iconCalendarPlusWhite,
    //   visible: assignedCoach.value.calcomUsername,
    //   action: () => openCalcomModal(assignedCoach.value.calcomUsername)
    // },
    // {
    //   label: "Change My Coach",
    //   icon: iconChange,
    //   iconWhite: iconChangeWhite,
    //   visible: true,
    //   action: () => openUrl(ZIGPOLL_LINK)
    // }
  ]

  return ctaData.filter((e) => e.visible)
})

// FUNCTIONS
function clickOnTakeTest() {
  router.push({ name: GlobalVariables.urls.report.children.testRetake.routeName })
}

// function openUrl(redirectUrl: string) {
//   window.open(redirectUrl, "_blank")
// }

// function openCalcomModal(calcomUsername: string | null | undefined) {
//   if (!calcomUsername) return
//   data.calcomEmbedLink = UrlService.generateCalComEmbedLink(`${calcomUsername}/on-demand`)
//   data.showCalcomEmbedModal = true
// }

const bookingSuccessful = () => {
  localStorage.removeItem("checklist-reminder-checked")
  firePaymentAction(
    isExecutiveSubscribedOrCanceled.value || isExecutiveOnce.value
      ? StripePrice.COACHING_EXECUTIVE_EXTRA_SESSION
      : StripePrice.COACHING_EXTRA_SESSION_HIGH,
    UserFlow.COACHING
  )
}

const goToSelectPlan = () => {
  router.push({
    name: userStore.isExecutiveCoachingPotential
      ? GlobalVariables.urls.coaching.children.choosePlan.routeName
      : GlobalVariables.urls.coaching.children.selectPlan.routeName
  })
}

const fetchAssignedCoachReviews = async () => {
  try {
    const response = await api.coach.getCoachReviews(assignedCoach.value.id, {
      orderBy: "date",
      direction: "desc",
      minRating: 4
    })
    data.assignedCoachReviews = response.reviews
  } catch {
    console.error(`Error fetching user's assigned coach reviews. Assigned Coach: ${assignedCoach.value.id}`)
  }
}

const firePaymentAction = async (stripePrice: StripePrice, userFlow: UserFlow) => {
  const stripePrices = [stripePrice]
  const paymentAction = getPaymentAction(stripePrices, userFlow)

  try {
    await paymentAction()
  } catch (error) {
    console.error(`Failed to create checkout session for ${stripePrice}`, error)
  }
}

const getInitials = (fullName: string) => {
  const name = fullName.trim()
  if (!name) return ""

  const nameArray = name.split(" ")
  const arrLength = nameArray.length

  const firstInitial = nameArray[0][0]
  const lastInitial = arrLength > 1 ? nameArray[arrLength - 1][0] : ""

  return `${firstInitial}${lastInitial}`.toUpperCase()
}

// LIFECYCLE HOOKS
onMounted(async () => {
  if (!coachingStore.hasAssignedCoach) await coachingStore.loadAssignedCoach()
  if (coachingStore.hasAssignedCoach) await fetchAssignedCoachReviews()
})
</script>

<style lang="scss" scoped>
.i-coach-profile-details {
  .main {
    @include flex($direction: column, $justify: start, $align: stretch, $gap: 25px);

    width: max-content;

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, space-between, $align: center, $gap: 20px);

      width: 100%;
    }

    .left {
      @include flex(row, start, center, $gap: 10px);

      @media screen and (min-width: $breakpoint-sm) {
        gap: 20px;
      }

      .image-wrap {
        position: relative;

        .image {
          @include border-radius-rounded;

          width: 60px;
          height: auto;

          @media screen and (min-width: $breakpoint-xs) {
            width: 100px;
          }
        }

        .play-video {
          @include border-radius-rounded;

          position: absolute;
          cursor: pointer;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
        }
      }

      .details {
        @include text-sm;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-md;
        }

        .coach {
          @include flex(row, start, start, $gap: 3px);
          @include text-md;

          font-weight: 700;
          color: $black950;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-lg;
          }

          img {
            width: 12px;
          }
        }

        .coach-email {
          color: $azure-radiance600;
        }

        .rating {
          @include text-sm;

          font-weight: 500;
          color: $bilbao500;

          img {
            vertical-align: baseline;
            width: 60px;

            @media screen and (min-width: $breakpoint-sm) {
              vertical-align: text-bottom;
              width: 95px;
            }
          }

          .review-cta {
            cursor: pointer;

            &:hover {
              color: $bilbao600;
            }
          }
        }
      }
    }

    .right {
      &.grey {
        @include flex(column, center, center, $gap: 12px);
        @include text-lg;
        @include border-radius-md;

        padding: 10px;
        font-weight: 600;
        color: $black;
        background-color: $black50;
        text-align: center;

        @media screen and (min-width: $breakpoint-sm) {
          padding: 20px;
          gap: 20px;
        }

        /* stylelint-disable-next-line media-feature-range-notation */
        @media screen and (min-width: 956px) {
          @include flex(row, space-between, center, $gap: 15px);

          text-align: start;
        }

        @media screen and (min-width: $breakpoint-lg) {
          @include flex(column, center, center, $gap: 12px);

          text-align: center;
        }

        @media screen and (min-width: $breakpoint-xl) {
          @include flex(row, space-between, center, $gap: 15px);

          text-align: start;
        }

        .resume {
          @include flex(column, center, center);

          flex: 1 0 min-content;

          @media screen and (min-width: $breakpoint-md) {
            align-items: stretch;
          }

          .tiny {
            @include text-xs;

            color: $comet800;
            font-weight: 400;
          }
        }
      }

      .ctas {
        position: relative;

        .surface {
          @include flex(row, start, center, $gap: 10px);
        }

        .wrapper {
          display: none;
          position: absolute;
          width: 100%;
          min-width: max-content;
          z-index: 600;
          top: calc(100% - 4px);

          &:hover {
            display: block;
          }

          .options {
            @include border-radius-md;
            @include flex(column, start, stretch);

            overflow: hidden;
            border: 1px solid $black200;
            box-shadow: 0 4px 16px 0 $black100;
            background-color: $white;
            padding: 5px 0;
            margin-top: 10px;
            width: max-content;

            .option {
              padding: 6px 12px;
              cursor: pointer;

              &:hover {
                background-color: $black50;
              }

              img {
                width: 22px;
                margin-right: 5px;
              }
            }
          }
        }

        .surface:has(> .more:hover) + .wrapper {
          display: block;
        }
      }
    }
  }

  .light-box-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    max-width: 840px;
    border-radius: 4px;
    overflow: hidden;
  }

  .embed {
    padding: 30px 0;

    @media screen and (min-width: $breakpoint-lg) {
      padding: unset;
    }

    .confirmation-modal {
      @include flex($direction: column, $justify: start, $align: center);

      margin: 10px;

      @media screen and (min-width: $breakpoint-md) {
        margin: 25px;
      }
    }
  }

  .review-modal {
    margin: 30px;

    .reviews-carousel {
      width: 100%;

      .review {
        @include flex($direction: column, $justify: start, $align: stretch, $gap: 15px);
        @include text-sm;

        padding: 15px;
        width: 100%;
        height: 100%;
        margin: 0 8px;
        text-align: left;
        border: 1px solid $zumthor100;
        border-radius: 20px;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-md;

          padding: 22px 25px;
        }

        .info {
          @include flex(row, start, center, $gap: 10px);

          font-weight: 400;

          .rev-image {
            @include border-radius-rounded;

            width: 35px;
            height: 35px;
            object-fit: cover;

            @media screen and (min-width: $breakpoint-sm) {
              width: 40px;
              height: 40px;
            }
          }

          .initials {
            font-weight: 700;
            color: $pickle-bluewood900;
            background-color: $brand-opacity100;
            width: 35px;
            height: 35px;

            @include flex(row, center, center);
            @include border-radius-rounded;

            @media screen and (min-width: $breakpoint-sm) {
              width: 40px;
              height: 40px;
            }
          }

          .user-info {
            @include flex(column, center, start);

            .location {
              @include flex(row, start, center, $gap: 5px);

              img {
                width: 20px;
                height: 20px;
              }
            }
          }
        }

        .star-date {
          @include flex(row, space-between, center, $gap: 10px);

          .stars {
            height: 18px;
          }
        }

        .body {
          @include flex(column, start, start, $gap: 5px);

          .progress {
            font-weight: 600;
          }

          .comment {
            font-weight: 400;
          }
        }
      }
    }
  }
}
</style>
<style lang="scss">
.i-coach-profile-details {
  /* stylelint-disable */
  .carousel__next,
  .carousel__prev {
    margin: 0 -25px;
  }
  /* stylelint-enable */
}
</style>
